<template>
    <div>
        <b-container fluid>
            <div class="my-3 mt-5">
                <div class="d-flex align-items-center justify-content-between">
                    <div class="h3 m-0 mr-3 ml-3">
                        Reports
                    </div>
                </div>
            </div>

            <b-card class="mx-3 mb-4">
                <h5 class="mb-3">Run Report</h5>

                <div class="row align-items-end">
                    <div class="col-lg-8 mb-3 mb-lg-0">
                        <label for="report-type-select" class="font-weight-bold">Report</label>
                        <b-form-select
                            id="report-type-select"
                            v-model="selected_report_type"
                            :options="report_options"
                        />
                    </div>
                    <div class="col-lg-auto">
                        <b-button
                            variant="primary"
                            :disabled="!selected_report_type"
                            @click="openReportModal"
                        >
                            Configure Report
                        </b-button>
                    </div>
                </div>
            </b-card>

            <b-card class="mx-3 mb-4">
                <h5 class="mb-3">Recent Requests</h5>

                <div>
                    <div class="d-flex align-items-center mb-3">
                        <b-button
                            variant="light"
                            :class="report_filters_visible ? 'active' : 'collapsed'"
                            @click="report_filters_visible = !report_filters_visible"
                        >
                            <font-awesome-icon :icon="['fad', 'filter']" /> Filters
                        </b-button>

                        <div v-if="active_report_filters" class="ml-3">
                            <b-button variant="danger" @click="clearReportFilters">
                                <font-awesome-icon :icon="['fad', 'backspace']" /> Clear All Filters
                            </b-button>
                        </div>
                    </div>

                    <b-collapse v-model="report_filters_visible">
                        <div class="row align-items-end mb-3">
                            <div class="col-xl col-md-6 mt-2 mt-xl-0">
                                <label for="report-type-filter" class="font-weight-bold">Report Type</label>
                                <b-form-select
                                    id="report-type-filter"
                                    v-model="report_filters.report_type"
                                    :options="report_type_filter_options"
                                />
                            </div>

                            <div class="col-xl col-md-6 mt-2 mt-xl-0">
                                <label for="report-date-filter" class="font-weight-bold">Date Requested</label>
                                <b-form-input
                                    id="report-date-filter"
                                    v-model="report_filters.requested_date"
                                    type="date"
                                />
                            </div>

                            <div class="col-xl col-md-6 mt-2 mt-xl-0">
                                <label for="report-requester-filter" class="font-weight-bold">Requested By</label>
                                <b-form-select
                                    id="report-requester-filter"
                                    v-model="report_filters.requested_by"
                                    :options="report_requester_filter_options"
                                />
                            </div>

                            <div class="col-xl col-md-6 mt-2 mt-xl-0">
                                <label for="report-status-filter" class="font-weight-bold">Status</label>
                                <b-form-select
                                    id="report-status-filter"
                                    v-model="report_filters.status"
                                    :options="report_status_filter_options"
                                />
                            </div>

                            <div class="col-xl col-md-12 mt-2 mt-xl-0">
                                <label for="report-comment-filter" class="font-weight-bold">Comment</label>
                                <b-form-input
                                    id="report-comment-filter"
                                    v-model.trim="report_filters.comment"
                                    type="search"
                                    placeholder="Filter By Comment"
                                />
                            </div>
                        </div>
                    </b-collapse>

                    <b-table
                        small
                        responsive
                        outlined
                        show-empty
                        head-variant="light"
                        :items="paged_report_requests"
                        :fields="request_fields"
                        :empty-text="report_requests_empty_text"
                    >
                        <template #cell(report_type)="data">
                            {{ reportLabel(data.item.report_type) }}
                        </template>

                        <template #cell(requested_by)="data">
                            {{ requestedByLabel(data.item) }}
                        </template>

                        <template #cell(publication)="data">
                            <span v-if="data.item.publication">
                                {{ data.item.publication.sku }} - {{ data.item.publication.title }}
                            </span>
                            <span v-else class="text-muted">Global</span>
                        </template>

                        <template #cell(status)="data">
                            <b-badge :variant="statusVariant(data.item.status)">
                                {{ data.item.status }}
                            </b-badge>
                        </template>

                        <template #cell(comment)="data">
                            <span
                                v-if="data.item.comment"
                                v-b-tooltip.hover
                                :title="data.item.comment"
                            >
                                {{ commentPreview(data.item.comment) }}
                            </span>
                            <span v-else class="text-muted">—</span>
                        </template>

                        <template #cell(created_at)="data">
                            {{ formatDate(data.item.created_at) }}
                        </template>

                        <template #cell(completed_at)="data">
                            {{ data.item.completed_at ? formatDate(data.item.completed_at) : '—' }}
                        </template>

                        <template #cell(download)="data">
                            <b-button
                                v-if="data.item.status === 'complete'"
                                size="sm"
                                variant="outline-primary"
                                @click="downloadReport(data.item)"
                            >
                                Download
                            </b-button>
                            <span v-else-if="data.item.status === 'failed'" class="text-danger">
                                Failed
                            </span>
                            <span v-else class="text-muted">
                                Pending
                            </span>
                        </template>
                    </b-table>

                    <div
                        v-if="filtered_report_requests.length"
                        class="d-flex align-items-center justify-content-between mt-3"
                    >
                        <div class="d-flex align-items-center">
                            <span class="mr-2">Rows per page</span>
                            <b-form-select
                                v-model="reports_per_page"
                                :options="reports_per_page_options"
                                size="sm"
                                style="width: 90px;"
                            />
                        </div>

                        <b-pagination
                            v-model="reports_current_page"
                            :per-page="reports_per_page"
                            :total-rows="filtered_report_requests.length"
                            align="right"
                            size="sm"
                            class="mb-0"
                        />
                    </div>
                </div>
            </b-card>

            <report-request-modal
                :value="show_report_modal"
                :report-type="selected_report_type"
                :report-label="selected_report_label"
                :publication-options="publication_options"
                :loading-publications="loading_publications"
                :submitting="isGeneratingSelectedReport"
                @hide="show_report_modal = false"
                @submit="submitSelectedReport"
            />
        </b-container>
    </div>
</template>

<script>
import {mapState} from 'vuex'
import ReportRequestModal from '@/components/modals/reports/ReportRequestModal'
import {downloadBlob} from '@/common/fileDownload'

export default {
    name: 'ReportsView',
    components: {
        ReportRequestModal,
    },
    data: () => ({
        loading_publications: false,
        generating_report: false,
        show_report_modal: false,
        downloading_report_ids: [],
        publications: [],
        selected_report_type: null,
        report_requests: [],
        report_filters_visible: false,
        report_filters: {
            report_type: '',
            requested_date: '',
            requested_by: '',
            status: '',
            comment: '',
        },
        reports_current_page: 1,
        reports_per_page: 25,
        reports_per_page_options: [10, 25, 50, 100],
        poller: null,
        seen_statuses: {},
        request_fields: [
            { key: 'id', label: 'Request ID' },
            { key: 'report_type', label: 'Report' },
            { key: 'requested_by', label: 'Requested By' },
            { key: 'publication', label: 'Publication' },
            { key: 'status', label: 'Status' },
            { key: 'comment', label: 'Comment' },
            { key: 'created_at', label: 'Requested' },
            { key: 'completed_at', label: 'Completed' },
            { key: 'download', label: '' },
        ],
    }),
    computed: {
        ...mapState({
            user: state => state.user.user,
        }),
        publication_options() {
            const options = [{
                value: null,
                text: this.loading_publications
                    ? 'Loading publications...'
                    : this.narration_report_selected
                        ? 'All Publications (Global)'
                        : 'Select a publication',
            }]

            return options.concat(this.publications.map((publication) => ({
                value: publication.id,
                text: `${publication.sku} - ${publication.title}`,
            })))
        },
        narration_report_selected() {
            return [
                'articles_with_narration',
                'articles_without_narration',
            ].includes(this.selected_report_type)
        },
        report_labels() {
            return {
                untitled_panels: 'Untitled Panels',
                untitled_weeks: 'Untitled Weeks',
                articles_with_narration: 'Articles With Narration',
                articles_without_narration: 'Articles Without Narration',
                media_images_missing_alt: 'Media Images Missing Alt',
                media_videos_missing_captions: 'Media Videos Missing Captions',
                article_by_sku: 'Article By SKU',
                printables_by_sku: 'Printables By SKU',
                related_media_by_article_id: 'Related Media By Article ID',
                related_media_videos_by_sku: 'All Publication Related Media',
                related_media_locations_by_id: 'Related Media Locations By ID',
                weekly_phenomenon_by_unit: 'Weekly Phenomenon By Unit',
                batch_related_media_pedigree: 'Batch Media Pedigree',
            }
        },
        report_options() {
            return [
                { value: null, text: 'Select a report' },
                {
                    label: 'Publication Reports',
                    options: [
                        { value: 'article_by_sku', text: this.report_labels.article_by_sku },
                        { value: 'printables_by_sku', text: this.report_labels.printables_by_sku },
                        { value: 'related_media_videos_by_sku', text: this.report_labels.related_media_videos_by_sku },
                    ],
                },
                {
                    label: 'Unit Reports',
                    options: [
                        { value: 'weekly_phenomenon_by_unit', text: this.report_labels.weekly_phenomenon_by_unit },
                    ],
                },
                {
                    label: 'Week Reports',
                    options: [
                        { value: 'untitled_weeks', text: this.report_labels.untitled_weeks },
                    ],
                },
                {
                    label: 'Article Reports',
                    options: [
                        { value: 'articles_with_narration', text: this.report_labels.articles_with_narration },
                        { value: 'articles_without_narration', text: this.report_labels.articles_without_narration },
                        { value: 'related_media_by_article_id', text: this.report_labels.related_media_by_article_id },
                    ],
                },
                {
                    label: 'Media Reports',
                    options: [
                        { value: 'media_images_missing_alt', text: this.report_labels.media_images_missing_alt },
                        { value: 'media_videos_missing_captions', text: this.report_labels.media_videos_missing_captions },
                        { value: 'related_media_locations_by_id', text: this.report_labels.related_media_locations_by_id },
                        { value: 'batch_related_media_pedigree', text: this.report_labels.batch_related_media_pedigree },
                    ],
                },
                {
                    label: 'Panel Reports',
                    options: [
                        { value: 'untitled_panels', text: this.report_labels.untitled_panels },
                    ],
                },
            ]
        },
        selected_report_label() {
            return this.selected_report_type ? this.reportLabel(this.selected_report_type) : ''
        },
        isGeneratingSelectedReport() {
            return this.generating_report
        },
        active_report_filters() {
            return Object.values(this.report_filters).some((value) => value !== '')
        },
        report_type_filter_options() {
            return [
                { value: '', text: 'All Report Types' },
                ...this.report_options.slice(1),
            ]
        },
        report_requester_filter_options() {
            const currentUserId = this.user && this.user.id
            const requesters = this.report_requests
                .map((reportRequest) => reportRequest.requested_by)
                .filter((requester) => (
                    requester
                    && requester.id
                    && (!currentUserId || requester.id.toString() !== currentUserId.toString())
                ))
            const uniqueRequesters = _.uniqBy(requesters, 'id')
            const orderedRequesters = _.orderBy(uniqueRequesters, ['last_name', 'first_name'], ['asc', 'asc'])
            const options = [{ value: '', text: 'All Requesters' }]

            if (currentUserId) {
                options.push({ value: currentUserId, text: 'Myself' })
            }

            return options.concat(orderedRequesters.map((requester) => ({
                value: requester.id,
                text: this.requestedByLabel({ requested_by: requester }),
            })))
        },
        report_status_filter_options() {
            return [
                { value: '', text: 'All Statuses' },
                { value: 'queued', text: 'Queued' },
                { value: 'processing', text: 'Processing' },
                { value: 'complete', text: 'Complete' },
                { value: 'failed', text: 'Failed' },
            ]
        },
        filtered_report_requests() {
            const commentFilter = this.report_filters.comment.toLowerCase()

            return this.report_requests.filter((reportRequest) => {
                if (
                    this.report_filters.report_type
                    && reportRequest.report_type !== this.report_filters.report_type
                ) {
                    return false
                }

                if (
                    this.report_filters.requested_date
                    && this.moment(reportRequest.created_at).format('YYYY-MM-DD') !== this.report_filters.requested_date
                ) {
                    return false
                }

                if (
                    this.report_filters.requested_by
                    && (
                        !reportRequest.requested_by
                        || reportRequest.requested_by.id !== this.report_filters.requested_by
                    )
                ) {
                    return false
                }

                if (this.report_filters.status && reportRequest.status !== this.report_filters.status) {
                    return false
                }

                if (
                    commentFilter
                    && !(reportRequest.comment || '').toLowerCase().includes(commentFilter)
                ) {
                    return false
                }

                return true
            })
        },
        report_requests_empty_text() {
            return this.active_report_filters
                ? 'No report requests match the selected filters.'
                : 'No report requests yet.'
        },
        paged_report_requests() {
            const start = (this.reports_current_page - 1) * this.reports_per_page
            const end = start + this.reports_per_page

            return this.filtered_report_requests.slice(start, end)
        },
    },
    watch: {
        report_filters: {
            deep: true,
            handler() {
                this.reports_current_page = 1
            },
        },
        reports_per_page() {
            this.reports_current_page = 1
        },
        report_requests() {
            const totalPages = Math.max(1, Math.ceil(this.filtered_report_requests.length / this.reports_per_page))

            if (this.reports_current_page > totalPages) {
                this.reports_current_page = 1
            }
        },
    },
    methods: {
        formatDate(value) {
            return this.moment(value).format('MM-DD-YYYY h:mm A')
        },
        clearReportFilters() {
            this.report_filters = {
                report_type: '',
                requested_date: '',
                requested_by: '',
                status: '',
                comment: '',
            }
        },
        commentPreview(comment) {
            if (comment.length <= 50) {
                return comment
            }

            return `${comment.slice(0, 50)}...`
        },
        reportLabel(reportType) {
            return this.report_labels[reportType] || reportType
        },
        statusVariant(status) {
            if (status === 'complete') {
                return 'success'
            }

            if (status === 'failed') {
                return 'danger'
            }

            if (status === 'processing') {
                return 'warning'
            }

            return 'secondary'
        },
        requestedByLabel(reportRequest) {
            if (!reportRequest.requested_by) {
                return 'Unknown'
            }

            const requestedBy = reportRequest.requested_by
            const fullName = `${requestedBy.first_name || ''} ${requestedBy.last_name || ''}`.trim()

            return fullName || requestedBy.email || 'Unknown'
        },
        openReportModal() {
            if (!this.selected_report_type) {
                return
            }

            this.show_report_modal = true
        },
        async getPublications() {
            this.loading_publications = true

            try {
                const response = await this.axios.get('/api/publications')
                this.publications = _.orderBy(response.data, ['sku', 'title'], ['asc', 'asc'])
            } finally {
                this.loading_publications = false
            }
        },
        async getReportRequests() {
            const response = await this.axios.get('/api/reports/requests', {
                params: {
                    scope: 'all',
                },
            })
            const previousStatuses = this.seen_statuses

            this.report_requests = response.data
            this.seen_statuses = this.report_requests.reduce((statuses, request) => {
                statuses[request.id] = request.status
                return statuses
            }, {})

            this.report_requests.forEach((request) => {
                if (previousStatuses[request.id] && previousStatuses[request.id] !== 'complete' && request.status === 'complete') {
                    this.$bvToast.toast(`${this.reportLabel(request.report_type)} report is ready to download.`, {
                        title: 'Report Complete',
                        variant: 'success',
                        solid: false,
                        autoHideDelay: 5000,
                        appendToast: true,
                    })
                }
            })
        },
        async submitSelectedReport(form) {
            const payload = {
                ...form,
                report_type: this.selected_report_type,
            }

            if (payload.report_type === 'untitled_panels') {
                const filterFields = ['publication_id', 'unit_id', 'week_id', 'article_id']
                const hasFilters = filterFields.some((field) => {
                    return payload[field] !== undefined
                        && payload[field] !== null
                        && payload[field].toString().trim() !== ''
                })

                if (!hasFilters && payload.global !== true) {
                    const result = await this.$swal({
                        title: 'Run Global Report?',
                        text: 'No filters were provided. Run this report for all accessible panels?',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Run Report',
                        cancelButtonText: 'Cancel',
                    })

                    if (!result.isConfirmed) {
                        return
                    }

                    payload.global = true
                }
            }

            this.generating_report = true

            try {
                await this.axios.post('/api/reports', payload)
                await this.getReportRequests()
                this.show_report_modal = false
            } finally {
                this.generating_report = false
            }
        },
        async downloadReport(reportRequest) {
            if (_.includes(this.downloading_report_ids, reportRequest.id)) {
                return
            }

            this.downloading_report_ids.push(reportRequest.id)

            try {
                const response = await this.axios.get(
                    `/api/reports/download/${reportRequest.id}`,
                    { responseType: 'blob' }
                )
                downloadBlob(response.data, reportRequest.file_name || `report-${reportRequest.id}.csv`)
            } finally {
                this.downloading_report_ids = this.downloading_report_ids.filter((id) => id !== reportRequest.id)
            }
        }
    },
    async mounted() {
        await this.getPublications()
        await this.getReportRequests()

        this.poller = window.setInterval(() => {
            this.getReportRequests()
        }, 25000)
    },
    beforeDestroy() {
        if (this.poller) {
            window.clearInterval(this.poller)
        }
    }
}
</script>
